// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { formatErrorMessageForDisplay, getError, getUpdated } from '../../helpers';
import { globalMessage } from '../../utils/globalMessage';
import { ReleaseUpdatable } from '../../actions/updatable';
import { useCallUpdatable } from '../../utils/hooks/helpers';
import { StepRunUpdatable } from '../../actions/stepRun';
import { RunwayResponseType } from '../../types';
import { Nullable, StepRunComponentProps } from '../../typescriptHelpers';

interface UsePhasedReleaseMessagesProps {
    appId: string;
    releaseId: string;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

/**
 * Hook that shows success/error messages for phased release actions.
 * Must be used in a component that stays mounted when phased release completes
 * (e.g. PhasedReleaseModuleBase), otherwise the "Release to all users" success
 * message won't show because PhasedReleaseModuleCTAs unmounts when complete.
 */
export function usePhasedReleaseMessages(props: UsePhasedReleaseMessagesProps): void {
    const [, , pauseGoogleStagedReleaseResponseType, pauseGoogleStagedReleaseError] = useCallUpdatable(
        StepRunUpdatable.PauseGooglePhasedRelease,
        props.stepRunComponentProps,
    );
    const [, , completeGoogleStagedReleaseResponseTypeStepRun, completeGoogleStagedReleaseErrorStepRun] =
        useCallUpdatable(StepRunUpdatable.CompleteGooglePhasedRelease, props.stepRunComponentProps);
    const [, , pauseApplePhasedReleaseResponseTypeStepRun, pauseApplePhasedReleaseErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.PauseApplePhasedRelease,
        props.stepRunComponentProps,
    );
    const [, , resumeApplePhasedReleaseResponseTypeStepRun, resumeApplePhasedReleaseErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.ResumeApplePhasedRelease,
        props.stepRunComponentProps,
    );
    const [, , completeApplePhasedReleaseResponseTypeStepRun, completeApplePhasedReleaseErrorStepRun] =
        useCallUpdatable(StepRunUpdatable.CompleteApplePhasedRelease, props.stepRunComponentProps);
    const [, , pauseHuaweiPhasedReleaseResponseTypeStepRun, pauseHuaweiPhasedReleaseErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.PauseHuaweiPhasedRelease,
        props.stepRunComponentProps,
    );
    const [, , completeHuaweiPhasedReleaseResponseTypeStepRun, completeHuaweiPhasedReleaseErrorStepRun] =
        useCallUpdatable(StepRunUpdatable.CompleteHuaweiPhasedRelease, props.stepRunComponentProps);
    const [, , startStagedRolloutResponseTypeStepRun, startStagedRolloutErrorStepRun] = useCallUpdatable(
        StepRunUpdatable.StartAutomatedAndroidStagedRollout,
        props.stepRunComponentProps,
    );

    const haltGoogleStagedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.HaltGooglePhasedRelease, state, props.releaseId),
    );
    const haltGoogleStagedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.HaltGooglePhasedRelease, state, props.releaseId),
    );
    const completeGoogleStagedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.CompleteGooglePhasedRelease, state, props.releaseId),
    );
    const completeGoogleStagedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.CompleteGooglePhasedRelease, state, props.releaseId),
    );
    const pauseApplePhasedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.PauseApplePhasedRelease, state, props.releaseId),
    );
    const pauseApplePhasedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.PauseApplePhasedRelease, state, props.releaseId),
    );
    const completeApplePhasedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.CompleteApplePhasedRelease, state, props.releaseId),
    );
    const completeApplePhasedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.CompleteApplePhasedRelease, state, props.releaseId),
    );
    const resumeApplePhasedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.ResumeApplePhasedRelease, state, props.releaseId),
    );
    const resumeApplePhasedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.ResumeApplePhasedRelease, state, props.releaseId),
    );
    const pauseHuaweiPhasedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.PauseHuaweiPhasedRelease, state, props.releaseId),
    );
    const pauseHuaweiPhasedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.PauseHuaweiPhasedRelease, state, props.releaseId),
    );
    const completeHuaweiPhasedReleaseErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.CompleteHuaweiPhasedRelease, state, props.releaseId),
    );
    const completeHuaweiPhasedReleaseSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.CompleteHuaweiPhasedRelease, state, props.releaseId),
    );
    const startStagedRolloutErrorReleaseStep = useSelector((state: RootState) =>
        getError(ReleaseUpdatable.StartAutomatedStagedRollout, state, props.releaseId),
    );
    const startStagedRolloutSuccessReleaseStep = useSelector((state: RootState) =>
        getUpdated(ReleaseUpdatable.StartAutomatedStagedRollout, state, props.releaseId),
    );

    const pauseHuaweiPhasedReleaseError =
        props.stepRunComponentProps ? pauseHuaweiPhasedReleaseErrorStepRun : pauseHuaweiPhasedReleaseErrorReleaseStep;
    const pauseHuaweiPhasedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseHuaweiPhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   pauseHuaweiPhasedReleaseSuccessReleaseStep;
    const completeHuaweiPhasedReleaseError =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseErrorStepRun
        :   completeHuaweiPhasedReleaseErrorReleaseStep;
    const completeHuaweiPhasedReleaseSuccess =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeHuaweiPhasedReleaseSuccessReleaseStep;
    const haltGoogleStagedReleaseError =
        props.stepRunComponentProps ? pauseGoogleStagedReleaseError : haltGoogleStagedReleaseErrorReleaseStep;
    const haltGoogleStagedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseGoogleStagedReleaseResponseType === RunwayResponseType.Success
        :   haltGoogleStagedReleaseSuccessReleaseStep;
    const completeGoogleStagedReleaseError =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseErrorStepRun
        :   completeGoogleStagedReleaseErrorReleaseStep;
    const completeGoogleStagedReleaseSuccess =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeGoogleStagedReleaseSuccessReleaseStep;
    const pauseApplePhasedReleaseError =
        props.stepRunComponentProps ? pauseApplePhasedReleaseErrorStepRun : pauseApplePhasedReleaseErrorReleaseStep;
    const pauseApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   pauseApplePhasedReleaseSuccessReleaseStep;
    const completeApplePhasedReleaseError =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseErrorStepRun
        :   completeApplePhasedReleaseErrorReleaseStep;
    const completeApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeApplePhasedReleaseSuccessReleaseStep;
    const resumeApplePhasedReleaseError =
        props.stepRunComponentProps ? resumeApplePhasedReleaseErrorStepRun : resumeApplePhasedReleaseErrorReleaseStep;
    const resumeApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            resumeApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   resumeApplePhasedReleaseSuccessReleaseStep;
    const startStagedRolloutError =
        props.stepRunComponentProps ? startStagedRolloutErrorStepRun : startStagedRolloutErrorReleaseStep;
    const startStagedRolloutSuccess =
        props.stepRunComponentProps ?
            startStagedRolloutResponseTypeStepRun === RunwayResponseType.Success
        :   startStagedRolloutSuccessReleaseStep;

    React.useEffect(() => {
        if (pauseHuaweiPhasedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error pausing phased release', pauseHuaweiPhasedReleaseError),
            );
        }
    }, [pauseHuaweiPhasedReleaseError]);

    React.useEffect(() => {
        if (pauseHuaweiPhasedReleaseSuccess) {
            globalMessage.success('Phased release has been paused.');
        }
    }, [pauseHuaweiPhasedReleaseSuccess]);

    React.useEffect(() => {
        if (completeHuaweiPhasedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error rolling out to all users', completeHuaweiPhasedReleaseError),
            );
        }
    }, [completeHuaweiPhasedReleaseError]);

    React.useEffect(() => {
        if (completeHuaweiPhasedReleaseSuccess) {
            globalMessage.success('Your app has been rolled out to 100% of users.');
        }
    }, [completeHuaweiPhasedReleaseSuccess]);

    React.useEffect(() => {
        if (haltGoogleStagedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error halting staged release', haltGoogleStagedReleaseError),
            );
        }
    }, [haltGoogleStagedReleaseError]);

    React.useEffect(() => {
        if (haltGoogleStagedReleaseSuccess) {
            globalMessage.success('Staged rollout has been halted.');
        }
    }, [haltGoogleStagedReleaseSuccess]);

    React.useEffect(() => {
        if (completeGoogleStagedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error rolling out to all users', completeGoogleStagedReleaseError),
            );
        }
    }, [completeGoogleStagedReleaseError]);

    React.useEffect(() => {
        if (completeGoogleStagedReleaseSuccess) {
            globalMessage.success('Your app has been rolled out to 100% of users.');
        }
    }, [completeGoogleStagedReleaseSuccess]);

    React.useEffect(() => {
        if (pauseApplePhasedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error pausing phased release', pauseApplePhasedReleaseError),
            );
        }
    }, [pauseApplePhasedReleaseError]);

    React.useEffect(() => {
        if (pauseApplePhasedReleaseSuccess) {
            globalMessage.success('Your phased release has been paused.');
        }
    }, [pauseApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (completeApplePhasedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error releasing to all users', completeApplePhasedReleaseError),
            );
        }
    }, [completeApplePhasedReleaseError]);

    React.useEffect(() => {
        if (completeApplePhasedReleaseSuccess) {
            globalMessage.success('Your app has been released to all users.');
        }
    }, [completeApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (resumeApplePhasedReleaseError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error resuming phased release', resumeApplePhasedReleaseError),
            );
        }
    }, [resumeApplePhasedReleaseError]);

    React.useEffect(() => {
        if (resumeApplePhasedReleaseSuccess) {
            globalMessage.success('Your phased release has been resumed.');
        }
    }, [resumeApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (startStagedRolloutError) {
            globalMessage.error(
                formatErrorMessageForDisplay('Error starting automated staged rollout', startStagedRolloutError),
            );
        }
    }, [startStagedRolloutError]);

    React.useEffect(() => {
        if (startStagedRolloutSuccess) {
            globalMessage.success('Automated staged rollout has started!');
        }
    }, [startStagedRolloutSuccess]);
}
